import { useState } from "react";
import {
  CreateInsuranceTransactionDto,
  LineItemDto,
  ProcessedCorrectionBillingDto,
  RxPreviewDto,
} from "../../../../../../api-client";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { transactionMethods } from "../../../../Kasse/v2/types/kasse.types";
import { BillingTypeEnum } from "../../../Base/types/insuranceBills.types";
import { LineItemCorrectionBillInfoProps } from "../types/LineItemCorrectionBill.types";

/**
 * Computes the netValue of a Positionskorrektur selection, matching the backend
 * computeNetValueFromLineItems:
 *   netValue = sum(price) - sum(item.copayment)
 * No flat Rezeptgebühr — that is owed once per Verordnung and the original bill
 * already took it.
 */
function computeNetValueFromLineItems(items: LineItemDto[]): number {
  if (items.length === 0) return 0;

  const grossValue = items.reduce(
    (sum, item) => sum + Number(item.price || 0),
    0,
  );

  const copayment = items.reduce(
    (sum, item) => sum + Number(item.copayment || 0),
    0,
  );

  return Math.round((grossValue - copayment) * 100) / 100;
}

/**
 * An Rx's value before its positions are picked. rxPrice.netValue comes from
 * the central Rx pricing, which deducts the flat Rezeptgebühr; a
 * Positionskorrektur never carries it, not even with every position selected.
 */
function rxNetForCorrection(rx: any): number {
  return (
    Number(rx?.rxPrice?.netValue || 0) +
    Number(rx?.rxPrice?.copaymentFixedComponent || 0)
  );
}

const useLineItemCorrectionBillInfo = (
  props: LineItemCorrectionBillInfoProps,
) => {
  const {
    handleFullScreenDialogClose,
    onRowClick,
    account,
    userName,
    userId,
    onRxDelete,
    onValidationFilter,
    onBillsSaved,
    lineItemSelections,
    onLineItemSelectionChange,
  } = props;

  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [localSelectedRx, setLocalSelectedRx] = useState<any | null>(null);
  const [selectedRxId, setSelectedRxId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Will pass to BillCard Component
  const [correctionBills, setCorrectionBills] = useState<
    ProcessedCorrectionBillingDto[]
  >([]);

  // Store
  const TransactionsStore = useStore(TransactionsContext);
  const { createTransaction, prepareCorrectionBillsFromRxs } =
    TransactionsStore;

  // Kebab menu
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    rx: any,
  ) => {
    setAnchorEl(event.currentTarget);
    setLocalSelectedRx(rx);
  };
  const handleMenuClose = () => setAnchorEl(null);

  // Delete flow
  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };
  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setLocalSelectedRx(null);
  };

  function roundToNearestHundred(num: number): number {
    return Math.round(num * 100) / 100;
  }

  const handleDeleteConfirm = async () => {
    if (!localSelectedRx) return;

    try {
      const updatedBills = correctionBills
        .map((bill) => {
          const rxToDelete = bill.rxs.find(
            (rx) => rx.id === localSelectedRx.id,
          );

          if (rxToDelete) {
            const rxValue = rxNetForCorrection(rxToDelete);
            const updatedRxs = bill.rxs.filter(
              (rx) => rx.id !== localSelectedRx.id,
            );

            if (updatedRxs.length > 0) {
              return {
                ...bill,
                rxs: updatedRxs,
                correctionAmount: roundToNearestHundred(
                  bill.correctionAmount - rxValue,
                ),
              };
            }
            return null;
          }

          return bill;
        })
        .filter(Boolean) as ProcessedCorrectionBillingDto[];

      setCorrectionBills(updatedBills);
      setTableVisible(updatedBills.length > 0);

      // Clear line item selection for deleted Rx
      if (localSelectedRx.id && onLineItemSelectionChange) {
        onLineItemSelectionChange(localSelectedRx.id, []);
      }

      notificationStore.showMessage("Korrektur-Rezept entfernt", "success");
      onRxDelete?.(localSelectedRx.id);
    } catch (error: any) {
      let errorMessage =
        error?.response?.data?.message || error.message || "Unbekannter Fehler";
      console.error(
        "Error removing Rx from correction bills in handleDeleteConfirm",
        error,
      );
      notificationStore.showMessage(errorMessage, "error");
    } finally {
      setLocalSelectedRx(null);
      setOpenDeleteDialog(false);
    }
  };

  // Creating the bills from selected RXs from TransactionSpotlightSearch
  const handleRxsFromTransactionSelected = async (
    selectedRxPreviews: RxPreviewDto[],
  ) => {
    if (selectedRxPreviews.length === 0) return;

    setIsLoading(true);
    try {
      const rxIds = selectedRxPreviews.map((rx) => rx.id);

      // Check for duplicate RXs
      const existingRxIds = correctionBills.flatMap((bill) =>
        bill.rxs.map((rx) => rx.id),
      );
      const newRxIds = rxIds.filter((id) => !existingRxIds.includes(id));

      if (newRxIds.length === 0) {
        notificationStore.showMessage(
          "Alle ausgewählten Rezepte sind bereits hinzugefügt",
          "warning",
        );
        setIsLoading(false);
        return;
      }

      const newPreparedBills = await prepareCorrectionBillsFromRxs({
        rxIds: newRxIds,
        accountId: account,
      });

      if (newPreparedBills.length === 0) {
        notificationStore.showMessage(
          "Keine neuen Korrektur-Rechnungen gefunden",
          "warning",
        );
        setIsLoading(false);
        return;
      }

      // The prepare endpoint is shared with the whole-Rx correction flow, whose
      // amount has the flat Rezeptgebühr deducted; a Positionskorrektur has not.
      const preparedBills = newPreparedBills.map((bill) => ({
        ...bill,
        correctionAmount: roundToNearestHundred(
          bill.rxs.reduce((sum, rx) => sum + rxNetForCorrection(rx), 0),
        ),
      }));

      if (correctionBills.length === 0) {
        setCorrectionBills(preparedBills);
        setTableVisible(true);
        notificationStore.showMessage(
          `${preparedBills.length} Korrektur-Rechnungen wurden hinzugefügt`,
          "success",
        );
        setIsLoading(false);
        return;
      }

      // Merge new bills with existing ones based on payer and previousBillNumber
      const updatedBills = [...correctionBills];
      let addedRxCount = 0;

      preparedBills.forEach((newBill) => {
        const existingBillIndex = updatedBills.findIndex(
          (bill) =>
            bill.payer.ik === newBill.payer.ik &&
            bill.previousBillNumber === newBill.previousBillNumber,
        );

        if (existingBillIndex >= 0) {
          const existingBill = updatedBills[existingBillIndex];
          const newRxsToAdd = newBill.rxs.filter(
            (newRx) =>
              !existingBill.rxs.some(
                (existingRx) => existingRx.id === newRx.id,
              ),
          );

          if (newRxsToAdd.length > 0) {
            updatedBills[existingBillIndex] = {
              ...existingBill,
              rxs: [...existingBill.rxs, ...newRxsToAdd],
              correctionAmount:
                existingBill.correctionAmount +
                newRxsToAdd.reduce((sum, rx) => sum + rxNetForCorrection(rx), 0),
            };
            addedRxCount += newRxsToAdd.length;
          }
        } else {
          updatedBills.push(newBill);
          addedRxCount += newBill.rxs.length;
        }
      });

      setCorrectionBills(updatedBills);
      setTableVisible(true);

      notificationStore.showMessage(
        `${addedRxCount} Rezept(e) zu Korrektur-Rechnungen hinzugefügt`,
        "success",
      );
    } catch (error: any) {
      let errorMessage =
        error?.response?.data?.message || error.message || "Unbekannter Fehler";
      console.error("Error processing selected RXs from transaction", error);
      notificationStore.showMessage(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Notes update
  const handleNotesChange = (payerIK: string, val: string) => {
    setNotes((prev) => ({ ...prev, [payerIK]: val }));
  };

  /**
   * Recalculate correction amount for a specific bill when line items change.
   */
  const recalculateBillAmount = (
    rxId: string,
    selectedItems: LineItemDto[],
  ) => {
    setCorrectionBills((prev) =>
      prev.map((bill) => {
        const rxInBill = bill.rxs.find((rx) => rx.id === rxId);
        if (!rxInBill) return bill;

        // Recalculate netValue across all Rxs in this bill
        let totalAmount = 0;
        for (const rx of bill.rxs) {
          if (rx.id === rxId) {
            // Use the new selection for this Rx
            totalAmount += computeNetValueFromLineItems(selectedItems);
          } else {
            // Use the existing selection or the whole Rx
            const existingSelection = lineItemSelections[rx.id!];
            if (existingSelection && existingSelection.length > 0) {
              totalAmount += computeNetValueFromLineItems(existingSelection);
            } else {
              totalAmount += rxNetForCorrection(rx);
            }
          }
        }

        return {
          ...bill,
          correctionAmount: roundToNearestHundred(totalAmount),
        };
      }),
    );
  };

  /**
   * Handle line item selection change from LineItemSelector.
   * Updates parent state and recalculates bill amount.
   */
  const handleLineItemSelectionChange = (
    rxId: string,
    selectedItems: LineItemDto[],
  ) => {
    onLineItemSelectionChange(rxId, selectedItems);
    recalculateBillAmount(rxId, selectedItems);
  };

  const handleSave = async () => {
    if (correctionBills.length === 0) {
      notificationStore.showMessage(
        "Keine Korrektur-Rechnungen zum Speichern vorhanden",
        "error",
      );
      return;
    }

    setIsSaving(true);
    try {
      const savedIds: string[] = [];

      for (const correctionBill of correctionBills) {
        const { rxs, payer, correctionAmount } = correctionBill;
        const payerIK = payer.ik;
        const rxIds = rxs.map((rx) => rx.id!).filter(Boolean);

        // Build rxLineItemSelections from the lineItemSelections map
        const rxLineItemSelections = rxIds
          .filter(
            (rxId) =>
              lineItemSelections[rxId] && lineItemSelections[rxId].length > 0,
          )
          .map((rxId) => ({
            rxId,
            selectedLineItems: lineItemSelections[rxId],
          }));

        const dto: CreateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: account,
          amount: correctionAmount,
          userId,
          method: transactionMethods.VERSICHERUNG,
          rxIds: rxIds,
          notes: notes[payerIK] || "",
          paperCenterId: payer.paperReceivingCenter?.ik || "",
          dataCenterId: payer.digitalReceivingCenter?.ik || "",
          tAccountId: rxs[0]?.tarifGroupId === "Privat" ? 4105 : 4101,
          billType: BillingTypeEnum.LINE_ITEM_CORRECTION,
          rxLineItemSelections: rxLineItemSelections as any,
        };

        const result = await createTransaction(dto);
        if (result?.id) savedIds.push(result.id);
      }

      notificationStore.showMessage(
        "Positionskorrektur-Transaktionen erfolgreich erstellt",
        "success",
      );

      if (onBillsSaved && savedIds.length > 0) onBillsSaved(savedIds);

      // Reset UI
      setCorrectionBills([]);
      setTableVisible(false);
    } catch (error) {
      console.error("Error creating line item correction transactions", error);
      notificationStore.showMessage(
        "Fehler beim Erstellen der Positionskorrektur-Transaktionen",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Remove-all button
  const handleRemoveAll = async () => {
    setCorrectionBills([]);
    setTableVisible(false);
    notificationStore.showMessage(
      "Alle Korrektur-Rechnungen entfernt",
      "success",
    );
  };

  return {
    // states
    anchorEl,
    selectedRxId,
    openDeleteDialog,
    tableVisible,
    isLoading,
    isSaving,
    correctionBills,
    notes,

    // setters
    setSelectedRxId,

    // handlers
    handleSave,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleDeleteClick,
    handleMenuOpen,
    handleMenuClose,
    handleRemoveAll,
    handleNotesChange,
    handleRxsFromTransactionSelected,
    handleLineItemSelectionChange,

    // passthrough props
    onRowClick,
    userName,
    handleFullScreenDialogClose,
    account,
    userId,
    onRxDelete,
    onValidationFilter,
  };
};

export default useLineItemCorrectionBillInfo;
